import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from '../components/ui/Spinner';
import { PageHeader } from '../components/ui/PageHeader';
import { useApi } from '../hooks/useApi';
import { useRefetchOnFocus } from '../hooks/useRefetchOnFocus';
import type { DashboardAnalytics, AgentSummary, RunnerSummary, AnalyticsAgent, AnalyticsRange } from '@pantheon/shared';

// ── formatters ──────────────────────────────────────────────────────────────
function fmtTokens(n: number): string {
  if (n >= 1e9) return (n / 1e9).toFixed(1) + 'B';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return String(Math.round(n));
}
function fmtHours(n: number): string {
  return n.toFixed(1) + 'h';
}
function shortDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Display order: the SDLC pipeline, overseer (Athena) first.
const AGENTS: AnalyticsAgent[] = ['athena', 'hermes', 'daedalus', 'argus', 'prometheus'];
const AGENT_FILL: Record<AnalyticsAgent, string> = {
  hermes: 'var(--color-hermes)',
  daedalus: 'var(--color-daedalus)',
  prometheus: 'var(--color-prometheus)',
  athena: 'var(--color-athena)',
  argus: 'var(--color-argus)',
};
const AGENT_DOT: Record<AnalyticsAgent, string> = {
  hermes: 'bg-hermes',
  daedalus: 'bg-daedalus',
  prometheus: 'bg-prometheus',
  athena: 'bg-athena',
  argus: 'bg-argus',
};
const title = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const RANGE_LABELS: Record<AnalyticsRange, string> = {
  yesterday: 'Yesterday',
  '7d': '7 days',
  '30d': '30 days',
  '60d': '60 days',
  '90d': '90 days',
};
const RANGES: AnalyticsRange[] = ['yesterday', '7d', '30d', '60d', '90d'];

// ── chart scale helpers ──────────────────────────────────────────────────────
function niceStep(x: number): number {
  if (x <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(x)));
  const f = x / pow;
  const nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nf * pow;
}
// Round a value up to a clean number just above it (tight headroom) — used for a shared
// axis max across the runner charts so their bars are comparable at a glance.
function niceCeil(x: number): number {
  if (x <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(x)));
  const f = x / pow;
  const nf = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find((s) => s >= f - 1e-9) ?? 10;
  return nf * pow;
}
function computeScale(rawMax: number, integer: boolean, tickCount = 4, hardMax?: number): { max: number; ticks: number[] } {
  // A hard max pins the axis (e.g. 24h in a day) with evenly divided ticks.
  if (hardMax != null) {
    const step = hardMax / tickCount;
    return { max: hardMax, ticks: Array.from({ length: tickCount + 1 }, (_, t) => step * t) };
  }
  const step = rawMax <= 0 ? 1 : integer ? Math.max(1, Math.ceil(rawMax / tickCount)) : niceStep(rawMax / tickCount);
  const max = step * tickCount;
  return { max, ticks: Array.from({ length: tickCount + 1 }, (_, t) => step * t) };
}

interface ChartSeries {
  key: string;
  label: string;
  color: string;
  values: number[];
}

/**
 * A responsive HTML bar chart with a labeled Y-axis, gridlines, an X-axis of dates,
 * and an instant hover overlay (no native <title> delay). Single-series charts render
 * plain bars; multi-series render a stacked bar with a per-series tooltip breakdown.
 */
function BarChart({
  days,
  series,
  integer = false,
  formatValue = (n: number) => String(Math.round(n)),
  heightPx = 190,
  yMax,
}: {
  days: string[];
  series: ChartSeries[];
  integer?: boolean;
  formatValue?: (n: number) => string;
  heightPx?: number;
  /** Pin the Y-axis to this max (e.g. 24 for hours-in-a-day) instead of auto-scaling. */
  yMax?: number;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const n = days.length;
  const totals = days.map((_, i) => series.reduce((s, ser) => s + (ser.values[i] || 0), 0));
  const { max, ticks } = computeScale(Math.max(...totals, 0), integer, 4, yMax);
  const multi = series.length > 1;

  // Evenly-spaced X ticks.
  const xIdxs = [...new Set([0, Math.floor(n / 4), Math.floor(n / 2), Math.floor((3 * n) / 4), n - 1])].filter((i) => i >= 0);

  const clampedLeft = hover === null ? 0 : Math.min(94, Math.max(6, ((hover + 0.5) / n) * 100));

  return (
    <div className="pt-5">
      <div className="flex" style={{ height: heightPx }}>
        {/* Y axis */}
        <div className="relative w-12 shrink-0 text-[10px] text-gray-500 tabular-nums">
          {ticks.map((t, ti) => (
            <div key={ti} className="absolute right-1.5 -translate-y-1/2" style={{ bottom: `${(t / max) * 100}%` }}>
              {formatValue(t)}
            </div>
          ))}
        </div>
        {/* Plot */}
        <div className="relative flex-1" onMouseLeave={() => setHover(null)}>
          {ticks.map((t, ti) => (
            <div key={ti} className="absolute left-0 right-0 border-t border-slate-100" style={{ bottom: `${(t / max) * 100}%` }} />
          ))}
          <div className="absolute inset-0 flex items-stretch gap-[2px]">
            {days.map((_, i) => {
              const topIdx = series.reduce((acc, ser, si) => ((ser.values[i] || 0) > 0 ? si : acc), -1);
              return (
                <div key={i} className="relative flex-1 min-w-0" onMouseEnter={() => setHover(i)}>
                  {hover === i && <div className="absolute inset-0 bg-slate-100/70 rounded-sm" />}
                  {series.map((ser, si) => {
                    const v = ser.values[i] || 0;
                    if (v <= 0) return null;
                    const below = series.slice(0, si).reduce((s, x) => s + (x.values[i] || 0), 0);
                    const bottomPct = Math.min(100, (below / max) * 100);
                    const heightPct = Math.max(0, Math.min(100 - bottomPct, (v / max) * 100));
                    return (
                      <div
                        key={ser.key}
                        className={`absolute left-0 right-0 ${si === topIdx ? 'rounded-t-[3px]' : ''}`}
                        style={{ bottom: `${bottomPct}%`, height: `${heightPct}%`, background: ser.color }}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
          {/* Hover overlay */}
          {hover !== null && (
            <div
              className="absolute z-10 pointer-events-none -translate-x-1/2 bg-gray-900 text-white text-[11px] rounded-lg px-2.5 py-1.5 shadow-lg whitespace-nowrap"
              style={{ left: `${clampedLeft}%`, top: 2 }}
            >
              <div className="font-semibold mb-0.5">{shortDate(days[hover])}</div>
              {multi ? (
                <>
                  {series
                    .filter((ser) => (ser.values[hover] || 0) > 0)
                    .map((ser) => (
                      <div key={ser.key} className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ background: ser.color }} />
                        <span>{ser.label}</span>
                        <span className="ml-auto pl-3 tabular-nums">{formatValue(ser.values[hover] || 0)}</span>
                      </div>
                    ))}
                  <div className="flex items-center gap-1.5 mt-0.5 pt-0.5 border-t border-white/20">
                    <span>Total</span>
                    <span className="ml-auto pl-3 tabular-nums font-semibold">{formatValue(totals[hover])}</span>
                  </div>
                  {totals[hover] === 0 && <div className="text-white/60">no activity</div>}
                </>
              ) : (
                <div className="tabular-nums">{formatValue(totals[hover])}</div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* X axis */}
      <div className="flex mt-1.5">
        <div className="w-12 shrink-0" />
        <div className="relative flex-1 h-3 text-[10px] text-gray-500 tabular-nums">
          {xIdxs.map((i) => (
            <span key={i} className="absolute -translate-x-1/2" style={{ left: `${Math.min(96, Math.max(4, ((i + 0.5) / n) * 100))}%` }}>
              {shortDate(days[i])}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent = 'text-gray-900', sub }: { label: string; value: string; accent?: string; sub?: string }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-4">
      <p className={`text-2xl font-bold tabular-nums ${accent}`}>{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
      {sub && <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function AgentCard({ a }: { a: AgentSummary }) {
  const total = Math.max(a.total, 1);
  const pct = (v: number) => `${(v / total) * 100}%`;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-2.5 h-2.5 rounded-full ${AGENT_DOT[a.agent]}`} />
        <h3 className="font-semibold text-gray-800">{title(a.agent)}</h3>
        <span className="ml-auto text-2xl font-bold tabular-nums text-gray-900">{a.total}</span>
      </div>
      <div className="flex h-2 rounded-full overflow-hidden bg-slate-100 mb-1.5">
        {a.completed > 0 && <div style={{ width: pct(a.completed) }} className="bg-success" />}
        {a.inFlight > 0 && <div style={{ width: pct(a.inFlight) }} className="bg-primary" />}
        {a.failed > 0 && <div style={{ width: pct(a.failed) }} className="bg-danger" />}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-500 mb-3">
        <span><span className="text-success font-medium">{a.completed}</span> done</span>
        <span><span className="text-primary font-medium">{a.inFlight}</span> in flight</span>
        <span><span className="text-danger font-medium">{a.failed}</span> failed</span>
      </div>
      <dl className="flex justify-between text-center">
        <div>
          <dt className="text-[11px] text-gray-500">Tokens</dt>
          <dd className="text-sm font-semibold tabular-nums text-gray-800">{fmtTokens(a.tokens)}</dd>
        </div>
        <div>
          <dt className="text-[11px] text-gray-500">Completion</dt>
          <dd className="text-sm font-semibold tabular-nums text-gray-800">{a.total ? Math.round((a.completed / a.total) * 100) : 0}%</dd>
        </div>
      </dl>
    </div>
  );
}

function RunnerCard({ r, days, tokenYMax }: { r: RunnerSummary; days: string[]; tokenYMax?: number }) {
  const hoursMap = new Map(r.activeHoursByDay.map((p) => [p.date, p.hours]));
  const tokensMap = new Map(r.tokensByDay.map((p) => [p.date, p.tokens]));
  const hoursSeries = days.map((d) => hoursMap.get(d) ?? 0);
  const tokensSeries = days.map((d) => tokensMap.get(d) ?? 0);
  const totalHours = r.activeHoursByDay.reduce((s, p) => s + p.hours, 0);
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-4">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className={`w-2.5 h-2.5 rounded-full ${r.online ? 'bg-success' : 'bg-slate-300'}`} />
        <h3 className="font-semibold text-gray-800 truncate">{r.runnerId}</h3>
        <span className={`text-[11px] px-2 py-0.5 rounded-full ${r.online ? 'bg-success-light text-success' : 'bg-slate-100 text-gray-500'}`}>
          {r.online ? r.status || 'online' : 'offline'}
        </span>
        {r.rateLimited && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-warning-light text-warning">
            Rate limited{r.rateLimitResetAt ? ` · resets ${new Date(r.rateLimitResetAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ''}
          </span>
        )}
        {r.commit && <span className="ml-auto text-[11px] font-mono text-gray-500">{r.commit}</span>}
      </div>

      <dl className="grid grid-cols-4 gap-2 text-center mb-3">
        <div>
          <dt className="text-[11px] text-gray-500">Sessions</dt>
          <dd className="text-sm font-semibold tabular-nums text-gray-800">{r.sessions}</dd>
        </div>
        <div>
          <dt className="text-[11px] text-gray-500">Tokens</dt>
          <dd className="text-sm font-semibold tabular-nums text-gray-800">{fmtTokens(r.tokens)}</dd>
        </div>
        <div>
          <dt className="text-[11px] text-gray-500">Active</dt>
          <dd className="text-sm font-semibold tabular-nums text-gray-800">{fmtHours(totalHours)}</dd>
        </div>
        <div title={`${r.rateLimitedDays} of the window's days`}>
          <dt className="text-[11px] text-gray-500">Limited</dt>
          <dd className={`text-sm font-semibold tabular-nums ${r.rateLimitedPct > 0 ? 'text-warning' : 'text-gray-800'}`}>{r.rateLimitedPct}%</dd>
        </div>
      </dl>

      <div className="space-y-3">
        <div>
          <p className="text-[11px] text-gray-500 mb-2">Active hours / day</p>
          <BarChart days={days} series={[{ key: 'hours', label: 'Active', color: 'var(--color-daedalus)', values: hoursSeries }]} formatValue={fmtHours} heightPx={96} yMax={24} />
        </div>
        <div>
          <p className="text-[11px] text-gray-500 mb-2">Tokens / day</p>
          <BarChart days={days} series={[{ key: 'tokens', label: 'Tokens', color: 'var(--color-primary)', values: tokensSeries }]} formatValue={fmtTokens} heightPx={96} yMax={tokenYMax} />
        </div>
      </div>
    </div>
  );
}

function FilterSelect({ value, onChange, allLabel, options }: { value: string; onChange: (v: string) => void; allLabel: string; options: { id: string; name: string }[] }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="text-sm bg-white border border-slate-200 rounded-xl px-3 py-1.5 shadow-sm shadow-slate-200/50 text-gray-700 max-w-[15rem] focus:outline-none focus:ring-2 focus:ring-primary/30"
    >
      <option value="">{allLabel}</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>{o.name}</option>
      ))}
    </select>
  );
}

export function AnalyticsPage() {
  const { get } = useApi();
  const [data, setData] = useState<DashboardAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<AnalyticsRange>('30d');
  const [projectId, setProjectId] = useState('');
  const [initiativeId, setInitiativeId] = useState('');
  const getRef = useRef(get);
  getRef.current = get;

  const query = useMemo(() => {
    const p = new URLSearchParams({ range });
    if (projectId) p.set('projectId', projectId);
    if (initiativeId) p.set('initiativeId', initiativeId);
    return p.toString();
  }, [range, projectId, initiativeId]);

  const fetchData = useCallback(async () => {
    const res = await getRef.current<DashboardAnalytics>(`/dashboard/analytics?${query}`);
    if (res.success && res.data) setData(res.data);
    setLoading(false);
  }, [query]);

  useEffect(() => {
    setLoading(true);
    fetchData();
    const id = setInterval(fetchData, 30000);
    return () => clearInterval(id);
  }, [fetchData]);
  useRefetchOnFocus(fetchData);

  const header = (
    <PageHeader
      mark="A"
      markBg="bg-primary-light"
      markText="text-primary"
      title="Analytics"
      subtitle={data ? `Activity across the fleet · ${shortDate(data.startDate)} → ${shortDate(data.endDate)}` : 'Activity across the fleet.'}
    />
  );

  const filterBar = data && (
    <div className="flex flex-wrap items-center gap-2 mb-5">
      <div className="flex bg-white rounded-xl border border-slate-200 shadow-sm shadow-slate-200/50 p-0.5">
        {RANGES.map((r) => (
          <button
            key={r}
            onClick={() => setRange(r)}
            className={`text-sm px-3 py-1 rounded-lg transition-colors ${range === r ? 'bg-primary text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
          >
            {RANGE_LABELS[r]}
          </button>
        ))}
      </div>
      <div className="flex-1" />
      <FilterSelect value={projectId} onChange={setProjectId} allLabel="All projects" options={data.projects} />
      <FilterSelect value={initiativeId} onChange={setInitiativeId} allLabel="All initiatives" options={data.initiatives} />
    </div>
  );

  if (loading && !data) {
    return (
      <div className="animate-rise">
        {header}
        <div className="flex items-center justify-center py-16">
          <Spinner size="lg" className="text-primary" />
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="animate-rise">
        {header}
        <div className="text-center py-12 text-gray-400 bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50">No analytics data yet.</div>
      </div>
    );
  }

  const runnersOnline = data.runners.filter((r) => r.online).length;
  const activitySeries: ChartSeries[] = AGENTS.map((agent) => ({
    key: agent,
    label: title(agent),
    color: AGENT_FILL[agent],
    values: data.daily.map((d) => d.byAgent[agent] ?? 0),
  }));
  const tokenSeries: ChartSeries[] = [{ key: 'tokens', label: 'Tokens', color: 'var(--color-primary)', values: data.daily.map((d) => d.tokens) }];
  const jiraSeries: ChartSeries[] = [{ key: 'jira', label: 'Tickets done', color: 'var(--color-success)', values: data.daily.map((d) => d.jiraDone) }];
  const days = data.daily.map((d) => d.date);
  // Throughput cards follow the same pipeline order (Athena first).
  const orderedAgents = AGENTS.map((agent) => data.agents.find((a) => a.agent === agent)).filter((a): a is AgentSummary => !!a);
  // Shared token Y-axis across all runner charts so their bars are directly comparable.
  const peakRunnerTokens = Math.max(0, ...data.runners.flatMap((r) => r.tokensByDay.map((p) => p.tokens)));
  const runnerTokenYMax = peakRunnerTokens > 0 ? niceCeil(peakRunnerTokens) : undefined;

  return (
    <div className="animate-rise">
      {header}
      {filterBar}

      <div className="relative">
        {/* Fading overlay while a filter change refetches — gives instant feedback. */}
        <div
          className={`absolute inset-0 z-20 flex items-start justify-center pt-24 rounded-xl bg-white/60 backdrop-blur-[1px] transition-opacity duration-200 ${loading ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          <Spinner size="lg" className="text-primary" />
        </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <StatCard label="Sessions" value={String(data.totals.sessions)} />
        <StatCard label="Completed" value={String(data.totals.completed)} accent="text-success" />
        <StatCard label="Jira tickets" value={String(data.totals.jiraTickets)} accent="text-primary" />
        <StatCard
          label="Jira tickets done"
          value={String(data.totals.jiraTicketsDone)}
          accent="text-success"
          sub={data.totals.jiraTickets ? `${Math.round((data.totals.jiraTicketsDone / data.totals.jiraTickets) * 100)}% done` : undefined}
        />
        <StatCard label="Tokens" value={fmtTokens(data.totals.tokens)} accent="text-primary" />
        <StatCard label="Runners online" value={`${runnersOnline}/${data.runners.length}`} accent={runnersOnline > 0 ? 'text-success' : 'text-gray-900'} />
      </div>

      {/* Activity by agent */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-5 mb-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-sm font-semibold text-gray-700">Activity by agent <span className="font-normal text-gray-400">· sessions started per day</span></h2>
          <div className="flex items-center gap-3 text-[11px] text-gray-500 flex-wrap">
            {AGENTS.map((a) => (
              <span key={a} className="flex items-center gap-1.5">
                <span className={`w-2.5 h-2.5 rounded-full ${AGENT_DOT[a]}`} />
                {title(a)}
              </span>
            ))}
          </div>
        </div>
        <BarChart days={days} series={activitySeries} integer />
      </div>

      {/* Tokens per day */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-5 mb-8">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Tokens per day</h2>
        <BarChart days={days} series={tokenSeries} formatValue={fmtTokens} heightPx={160} />
      </div>

      {/* Jira tickets completed per day */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-5 mb-8">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Jira tickets completed <span className="font-normal text-gray-400">· per day (by session start day)</span></h2>
        <BarChart days={days} series={jiraSeries} integer heightPx={160} />
      </div>

      {/* Throughput by agent */}
      <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Throughput by agent</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-8">
        {orderedAgents.map((a) => (
          <AgentCard key={a.agent} a={a} />
        ))}
      </div>

      {/* Runners */}
      <div className="flex items-center gap-2 mb-2">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Runner utilization</h2>
        <span className="text-[11px] text-gray-400 italic">active hours derived from session durations</span>
      </div>
      {data.runners.length === 0 ? (
        <div className="text-center py-8 text-gray-400 bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 text-sm">No runners have reported yet.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {data.runners.map((r) => (
            <RunnerCard key={r.runnerId} r={r} days={days} tokenYMax={runnerTokenYMax} />
          ))}
        </div>
      )}

      <p className="text-[11px] text-gray-400 mt-6">
        Claude subscription usage limits aren't exposed as a live percentage by Claude Code — runners surface a rate-limit state and reset time when they hit a window, shown above.
      </p>
      </div>
    </div>
  );
}
